import { NgStyle, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    type OnInit,
    Renderer2,
    type Signal,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    ConfirmationService,
    type MenuItem,
    type MenuItemCommandEvent,
} from '@openng/optimus-ui/api';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MessageModule } from '@openng/optimus-ui/message';
import { PanelModule } from '@openng/optimus-ui/panel';
import { SelectModule } from '@openng/optimus-ui/select';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { orderBy, union } from 'lodash-es';
import { injectParams } from 'ngxtension/inject-params';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { PipelineRiskAssessmentComponent } from '@app/modules/assets/components/pipeline/pipeline-risk-assessment/pipeline-risk-assessment.component';
import type { FoundationModelService } from '@app/modules/assets/services/foundation-model.service';
import type { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import type { PromptService } from '@app/modules/assets/services/prompt.service';
import type { RagService } from '@app/modules/assets/services/rag.service';
import { ApprovalActionComponent } from '@app/shared/components/approval-review-workflow/approval-action/approval-action.component';
import { DescriptionComponent } from '@app/shared/components/description/description.component';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import {
    type ObjectDiffSummary,
    ObjectDiffViewerComponent,
} from '@app/shared/components/object-diff-viewer/object-diff-viewer.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import { TimelineComponent } from '@app/shared/components/timeline/timeline.component';
import { WorkflowSelectionComponent } from '@app/shared/components/workflow-selection/workflow-selection.component';
import {
    ACTIONS,
    APPROVAL_REVIEW_MAP,
    APPROVAL_TYPE,
    OBJECTS,
    OK_RESPONSE,
    PAGES,
    REVIEW_STATUS,
    SKELETON_TYPES,
    WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import type { DxDataGrid, DxDataGridColumn } from '@app/shared/interface/devextreme.interface';
import type { NgActivatedRoute } from '@app/shared/interface/ng-router.interface';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { CommonService } from '@app/shared/services/common.service';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import type { Review } from '@app/shared/services/mixins/workflow.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers, parseAsNumber } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { type RouteInfo, RouterHelper } from '@app/shared/utils/router-helper';

type EditorPreparingEventEx<TRowData = any, TKey = any> = DxDataGrid.EditorPreparingEvent<
    TRowData,
    TKey
> & {
    command?: string;
};

type ApprovalReviewMapValues = (typeof APPROVAL_REVIEW_MAP)[keyof typeof APPROVAL_REVIEW_MAP];

export type ApprovalReview = Review & {
    allowedActions?: ApprovalReviewMapValues[];
    isAllInputsSelected?: boolean;
    menuItems?: MenuItem[];
};

@Component({
    selector: 'cor-approval-review-workflow',
    templateUrl: './approval-review-workflow.component.html',
    styleUrls: ['./approval-review-workflow.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        LocalDatePipe,
        IncludesPipe,
        ApprovalActionComponent,
        StatusIconComponent,
        NoDataComponent,
        TextareaModule,
        DxDataGridModule,
        TimelineComponent,
        MenuModule,
        InputContainerComponent,
        DialogBoxComponent,
        SkeletonComponent,
        RouterModule,
        CheckboxModule,
        TooltipModule,
        WorkflowSelectionComponent,
        UniqueRgbColorPipe,
        NameBadgePipe,
        ButtonModule,
        NgStyle,
        TitleCasePipe,
        PanelModule,
        ObjectDiffViewerComponent,
        NgTemplateOutlet,
        SelectModule,
        TagModule,
        MessageModule,
        DescriptionComponent,
        AvatarModule,
        MessageModule,
        PipelineRiskAssessmentComponent,
    ],
})
export class ApprovalReviewWorkflowComponent implements OnInit {
    private readonly currentProject = injectParams<string>('projectSlug');
    private readonly route = inject(ActivatedRoute) as NgActivatedRoute<
        { object: OBJECTS },
        { id: number }
    >;

    readonly id = input(undefined, { transform: parseAsNumber });

    protected readonly object = toSignal(this.route.data.pipe(map(data => data.object)));
    protected readonly item = signal(null);
    protected readonly service: Signal<
        FoundationModelService | PromptService | RagService | PipelineService
    > = computed(() => this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.object()]));

    protected readonly isRejectingObject = signal(false);

    protected REVIEW_STATUS = REVIEW_STATUS;
    protected APPROVAL_TYPE = APPROVAL_TYPE;
    protected ACTIONS = ACTIONS;
    protected OBJECTS = OBJECTS;
    protected WORKFLOW_STATUS = WORKFLOW_STATUS;
    protected NON_EDITABLE_STATUS = [
        WORKFLOW_STATUS.APPROVED.valueOf(),
        WORKFLOW_STATUS.REJECTED.valueOf(),
    ];
    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    private readonly NON_EDITABLE_REVIEW_STATUS = [
        REVIEW_STATUS.ACCEPTED.valueOf(),
        REVIEW_STATUS.CANCELED.valueOf(),
    ];

    protected readonly summaryDatasource = signal<ApprovalReview[]>([]);
    protected readonly isSummaryDataLoading = signal(true);
    protected readonly isStatusUpdatePopupVisible = signal(false);
    protected readonly updateStatusPopUpTitle = signal('');
    // Used for sending reminders and any popup where we show "save changes"
    protected readonly isSaving = signal(false);
    protected reviewPayload: {
        type: APPROVAL_TYPE;
        status: REVIEW_STATUS;
        responsibilityId: number;
        id: number;
        reviewerId: number;
        reviewName: string;
        tool?: string;
    };
    protected columns: DxDataGridColumn[] = [];
    protected readonly parentGroupedApprovalLink = signal<string>(null);
    protected readonly groupApprovalParentObject = signal<Review['groupApproval']>(null);

    protected readonly lineageDiff = {
        diffData: signal<ObjectDiffSummary[]>([]),
        isLoading: signal(false),
        versionList: signal<Array<{ id: number; version: number; displayName: string }>>([]),
        selectedVersionId: signal<number | null>(null),
    };

    protected readonly commentForm =
        signal<
            FormGroup<{
                comment: FormControl<string>;
                isAccepted?: FormControl<boolean>;
            }>
        >(null);

    protected approvalWorkflow = {
        workflowIdControl: new FormControl<number>(null),
        isSaving: signal(false),
    };

    /**
     * Checks whether approval or review can be initiated.
     * Approval: Workflow should be assigned
     * Review: Object should be Approved
     */
    protected readonly isApprovalAllowed = computed(() => !!this.item()?.workflowId);

    private readonly commonService = inject(CommonService);
    private readonly injector = inject(Injector);
    private readonly router = inject(Router);
    private readonly toast = inject(ToastNotificationService);
    private readonly renderer = inject(Renderer2);
    protected readonly appConfigService = inject(AppConfigService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly confirmationService = inject(ConfirmationService);

    ngOnInit() {
        if (Number(this.id())) {
            // FIXME: Had to add any here because the subscribe was getting confused about the type
            (this.service().getById(this.id(), { auth: true }) as Observable<any>)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ result }) => {
                    this.item.set(result);
                    this.approvalWorkflow.workflowIdControl.patchValue(this.item().workflowId);
                    // Enable/disable the workflow control based on write access
                    if (this.item()?.actions?.[ACTIONS.WRITE]) {
                        this.approvalWorkflow.workflowIdControl.enable();
                    } else {
                        this.approvalWorkflow.workflowIdControl.disable();
                    }
                    this.initialize();
                });
        }
    }

    /**
     * Updates the approval workflow of the object
     */
    protected setApprovalWorkflow() {
        // Only allow workflow updates when object is in draft status
        if (this.item()?.currentStatus !== WORKFLOW_STATUS.DRAFT) {
            return;
        }

        this.approvalWorkflow.isSaving.set(true);
        // FIXME: Had to add any here because the subscribe was getting confused about the type
        (
            this.service().patch({
                workflowId: this.approvalWorkflow.workflowIdControl.value,
                id: this.id(),
            }) as Observable<any>
        )
            .subscribe(response => {
                this.item.update(item => ({ ...item, workflowId: response.result.workflowId }));
                this.getWorkflowDetails();
                this.toast.showSuccess('Approval Workflow has been updated.');
            })
            .add(() => this.approvalWorkflow.isSaving.set(false));
    }

    /**
     * @deprecated Avoid using this function, and prefer RouteHelper.buildRoute()
     * We should remove this function soon once we move all places calling this to buildRoute
     */
    getUIRoute(id: number, objectType: OBJECTS) {
        const routeData: RouteInfo = {
            id,
            object: objectType,
            tabRoutes: TAB_ROUTES.APPROVALS,
        };

        return RouterHelper.buildRoute(routeData);
    }

    private getCommentForm(status?: REVIEW_STATUS, isGrouped?: boolean) {
        this.commentForm.set(
            new FormGroup({
                comment: new FormControl(null, Validators.required),
            }),
        );

        // In case of grouped approval, add the isAccepted control
        if (
            status &&
            isGrouped &&
            [REVIEW_STATUS.ACCEPTED, REVIEW_STATUS.REJECTED].includes(status)
        ) {
            this.commentForm().addControl(
                'isAccepted',
                new FormControl(false, Validators.requiredTrue),
            );
        }
    }

    initialize() {
        this.getWorkflowDetails();
        this.columns = [
            {
                dataField: 'type',
                cellTemplate: 'typeTemplate',
            },
            {
                dataField: 'responsibilityName',
                cellTemplate: 'tagTemplate',
            },
            {
                dataField: 'activeReviewers',
                cellTemplate: 'activeReviewersTemplate',
            },
            {
                dataField: 'status',
                cellTemplate: 'statusTemplate',
            },
            {
                dataField: 'statusDate',
                cellTemplate: 'dateTemplate',
            },
            {
                dataField: 'allowedActions',
                caption: 'Actions',
                cellTemplate: 'actionTemplate',
            },
        ];
    }

    private onSendReminder(data: { id: number; activeReviewers: string[] }) {
        this.isSaving.set(true);
        // If tool is a reviewer, then sending the tool name, reviewerIds otherwise.
        const statusObj = { reviewId: data.id };
        this.service()
            .sendReminder(this.item().id, statusObj)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                if (response.result?.status === OK_RESPONSE) {
                    this.toast.showSuccess(
                        `${data?.activeReviewers.join(',')} ${'has been notified.'}`,
                    );
                }
            })
            .add(() => this.isSaving.set(false));
    }

    private onReviewStatusUpdate(status: REVIEW_STATUS, data: ApprovalReview) {
        const isGrouped = data.inputs?.length > 0;
        this.getCommentForm(status, isGrouped);

        this.updateStatusPopUpTitle.set(
            status === REVIEW_STATUS.ADD_COMMENT ? 'Add Comment' : `Change status to: ${status}`,
        );

        // THis payload is being prepared here, and will be send to the API aft erht isStatusUpdatePopup is closed
        this.reviewPayload = {
            status,
            responsibilityId: data.responsibilityId,
            id: data.id,
            reviewerId: data.reviewerId,
            reviewName: data.reviewName,
            type: isGrouped ? APPROVAL_TYPE.GROUPED : APPROVAL_TYPE.INDIVIDUAL,
        };

        this.isStatusUpdatePopupVisible.set(true);
    }

    protected onSaveClick() {
        if (!this.commentForm().valid) {
            FormHelper.showFormErrors(this.commentForm(), this.toast);

            return;
        }
        this.isSaving.set(true);

        const ob$: Observable<any> =
            this.reviewPayload.status === REVIEW_STATUS.ADD_COMMENT
                ? this.service().postReviewComment(
                      this.reviewPayload.id,
                      this.commentForm().controls.comment.value,
                  )
                : this.service().updateReviewStatus(this.item()?.id, {
                      entityId: this.item()?.id,
                      comment: this.commentForm().controls.comment.value,
                      ...this.reviewPayload,
                  });

        ob$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.getReviewDetails();
                this.isStatusUpdatePopupVisible.set(false);

                this.commonService.showToastForStatusChange(this.reviewPayload?.status);

                if (
                    [
                        REVIEW_STATUS.ACCEPTED,
                        REVIEW_STATUS.REJECTED,
                        REVIEW_STATUS.NEED_CHANGES,
                        REVIEW_STATUS.NEED_INFO,
                        REVIEW_STATUS.PENDING_ACCEPTANCE,
                        REVIEW_STATUS.CANCELED,
                        REVIEW_STATUS.ADD_COMMENT,
                    ].includes(this.reviewPayload?.status)
                ) {
                    // we need to refresh the page because there could be
                    // change in the workflow status on row action
                    RouterHelper.reloadPage(this.router, this.route);
                }
            })
            .add(() => {
                this.isSaving.set(false);
                this.ngOnInit();
            });
    }

    protected showRecallConfirmation() {
        const notCancelledItems: Review[] = (this.summaryDatasource() || []).filter(
            e => e.status !== REVIEW_STATUS.CANCELED,
        );

        const message = notCancelledItems.some(e => e.type === APPROVAL_TYPE.INDIVIDUAL)
            ? 'You are about to cancel all reviews and move the current version to back to Draft status, are you sure you want to continue?'
            : 'You are about to cancel all reviews and move the current version to back to Draft status. This cancellation will apply to all the objects included in this request, are you sure you want to continue?';

        this.confirmationService.confirm({
            key: 'confirm-dialog',
            message,
            closeOnEscape: true,
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            accept: () => {
                // Cancel all the requests and mark object as Rejected
                this.isRejectingObject.set(true);
                this.service()
                    .updateStatus(this.item().id, {
                        status: WORKFLOW_STATUS.DRAFT,
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(_ => {
                        this.toast.showSuccess('Object is reverted to Draft');
                        RouterHelper.reloadPage(this.router, this.route);
                    })
                    .add(() => {
                        this.isRejectingObject.set(false);
                    });
            },
        });
    }

    onCancelClick() {
        this.isStatusUpdatePopupVisible.set(false);
        this.reviewPayload = null;
        this.commentForm()?.reset();
        this.isSaving.set(false);
    }

    /**
     * fetches approval summary for the approval
     */
    private getReviewDetails() {
        this.service()
            .getReviewDetails(this.item()?.id)
            .subscribe(response => {
                const activeReview = (response.result || []).find(
                    review => review.status !== REVIEW_STATUS.CANCELED,
                );
                const groupApprovalParentObject =
                    activeReview?.type === APPROVAL_TYPE.GROUPED
                        ? (activeReview.groupApproval ?? null)
                        : null;
                this.groupApprovalParentObject.set(groupApprovalParentObject);
                if (groupApprovalParentObject) {
                    this.parentGroupedApprovalLink.set(
                        `${RouterHelper.getPageRoute(PAGES.APPROVALS, this.currentProject())}/${groupApprovalParentObject.objectType}/${groupApprovalParentObject.objectId}`,
                    );
                }
                this.summaryDatasource.set(response.result || []);

                this.summaryDatasource().forEach(item => {
                    item.isAllInputsSelected = false;
                    let actions: ApprovalReviewMapValues[] = [];

                    (item.inputs || []).forEach(inputObj => {
                        inputObj.link = RouterHelper.buildRoute({
                            object: inputObj.objectType,
                            id: inputObj.id,
                        });
                    });

                    const isEditableState = ![
                        ...this.NON_EDITABLE_REVIEW_STATUS,
                        ...this.NON_EDITABLE_STATUS,
                    ].includes(this.item()?.currentStatus);

                    const isReviewer = item?.isReviewer;
                    const isRequestor =
                        this.authenticationService.currentUser()?.name === item?.createdBy;
                    // bind actions when current status is editable and logged in user is same as reviewer or with
                    //  Approval access in case of approval.
                    if (isEditableState && this.item()?.actions?.[ACTIONS.WRITE]) {
                        /**
                         * Bind user allowed actions.
                         * In case of ongoing review, REJECT status shall be added.
                         * In case of approval, REJECT status shall be added only if
                         * the reviewer has veto power.
                         */
                        const userActions = this.getActions(item.status);

                        if (!Helpers.isEmpty(userActions)) {
                            // If the logged in user is a reviewer, adding reviewer
                            // allowed actions.
                            if (
                                isReviewer &&
                                this.permissionService.canAccessObject(
                                    Helpers.getPermissionName(this.object(), ACTIONS.APPROVE),
                                )
                            ) {
                                actions = union(actions, userActions?.reviewerActions);
                            }

                            // If the user is a requestor, then requestor allowed
                            // actions are being pushed
                            if (isRequestor || this.item()?.actions?.[ACTIONS.WRITE]) {
                                actions = union(actions, userActions?.requestorActions);

                                // Since the requestor is the reviewer as well, removing
                                // SEND_REMINDER action from the list.
                                if (isReviewer) {
                                    actions = actions.filter(
                                        action =>
                                            action.name !== APPROVAL_REVIEW_MAP.SEND_REMINDER.name,
                                    );
                                }
                            }

                            // If current user is not a requestor,
                            // then remove SEND_REMINDER and CANCEL_REQUEST
                            // actions from allowed actions list.
                            if (
                                this.authenticationService.currentUser()?.name !== item?.createdBy
                            ) {
                                actions = actions.filter(
                                    action =>
                                        action.name !== APPROVAL_REVIEW_MAP.SEND_REMINDER.name &&
                                        action.name !== APPROVAL_REVIEW_MAP.CANCEL_REQUEST.name,
                                );
                            }
                        }

                        actions = orderBy(actions, 'name');
                    }
                    item.allowedActions = [];

                    if (actions.length > 0) {
                        item.allowedActions = actions;
                        // Create menu items with commands
                        item.menuItems = actions.map(action => ({
                            label: action.name,
                            data: action,
                            command: (event: MenuItemCommandEvent) => {
                                const itemData = event.item.data as ApprovalReviewMapValues;
                                this.onActionClick(itemData, item);
                            },
                        }));
                    }
                });
            })
            .add(() => {
                this.isSummaryDataLoading.set(false);
            });
    }

    /**
     * Fetches lineage diff data for the current object
     */
    protected getLineageDiffData(): void {
        if (!this.item()?.id) return;

        this.lineageDiff.isLoading.set(true);
        this.service()
            .getLineageDiff(this.item().id, this.lineageDiff.selectedVersionId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                const objectOrder = [
                    OBJECTS.DATA_TABLE,
                    OBJECTS.FOUNDATION_MODEL,
                    OBJECTS.PROMPT,
                    OBJECTS.RAG,
                    OBJECTS.PIPELINE,
                    OBJECTS.REPORT,
                    OBJECTS.GLOBAL_FUNCTION,
                ];
                this.lineageDiff.diffData.set(
                    orderBy(result, [x => objectOrder.indexOf(x.object), 'name']),
                );
            })
            .add(() => {
                this.lineageDiff.isLoading.set(false);
            });
    }

    protected onEditorPreparing(editorPreparingEvent: EditorPreparingEventEx): void {
        if (
            editorPreparingEvent.command === 'select' &&
            editorPreparingEvent.parentType === 'dataRow'
        ) {
            // Disable the checkbox
            editorPreparingEvent.editorOptions.disabled = 1;
        }
    }

    /**
     * Hide and disable expandable btn for reviewType: "individual" items
     */
    paintExpandableBtn(element: DxDataGrid.RowPreparedEvent): void {
        if (this.getReviewType(element.key) === APPROVAL_TYPE.INDIVIDUAL) {
            const tableData = element.rowElement.querySelector('td:nth-child(1)');
            const div = element.rowElement.querySelector('td:nth-child(1)')?.querySelector('div');
            if (tableData && div) {
                this.renderer.removeClass(tableData, 'dx-datagrid-expand');
                this.renderer.removeClass(div, 'dx-datagrid-group-closed');
            }
        }
    }

    private getWorkflowDetails() {
        // Load versions and then compare the diff based on latest version
        (
            this.service().getAll({
                parentId: this.item().parentId,
                simple: true,
                // FIXME: Had to add type assertion here because the service Union typing gets confused
            }) as Observable<ListResponse<{ id: number; version: number }>>
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                const allVersions = response.result.map(object => ({
                    ...object,
                    displayName: `Version ${object.version}`,
                }));

                const currentIndex = allVersions.findIndex(v => v.id === this.item().id);

                // Keep only versions up to current version (inclusive)
                const filteredVersions =
                    currentIndex !== -1 ? allVersions.slice(0, currentIndex) : allVersions;

                // Initialize selected previous version (if exists)
                this.lineageDiff.selectedVersionId.set(
                    filteredVersions[filteredVersions.length - 1]?.id,
                );
                this.lineageDiff.versionList.set(filteredVersions.reverse());

                this.getLineageDiffData();
            });
        if (this.item()?.workflowId) {
            this.getReviewDetails();
        } else if (this.NON_EDITABLE_STATUS.includes(this.item()?.currentStatus)) {
            this.isSummaryDataLoading.set(false);
            // and do nothing
        } else {
            this.isSummaryDataLoading.set(false);
        }
    }

    onActionClick(itemData: ApprovalReviewMapValues, data: ApprovalReview) {
        if (itemData?.name === APPROVAL_REVIEW_MAP.SEND_REMINDER.name) {
            this.onSendReminder(data);

            return;
        }

        this.onReviewStatusUpdate(itemData?.action as REVIEW_STATUS, data);
    }

    /**
     * function to return necessary actions based on the item statuses.
     * @param status Item status.
     * @returns Set of necessary items will be returned.
     */
    getActions(status: REVIEW_STATUS): {
        requestorActions: ApprovalReviewMapValues[];
        reviewerActions: ApprovalReviewMapValues[];
    } {
        // List of allowed actions.
        const allowedActions: Partial<{
            [key in REVIEW_STATUS]: [ApprovalReviewMapValues[], ApprovalReviewMapValues[]];
        }> = {
            [REVIEW_STATUS.SCHEDULED]: [
                [APPROVAL_REVIEW_MAP.CANCEL_REQUEST, APPROVAL_REVIEW_MAP.ADD_COMMENT],
                [],
            ],
            [REVIEW_STATUS.PENDING_ACCEPTANCE]: [
                [
                    APPROVAL_REVIEW_MAP.SEND_REMINDER,
                    APPROVAL_REVIEW_MAP.CANCEL_REQUEST,
                    APPROVAL_REVIEW_MAP.ADD_COMMENT,
                ],
                [
                    APPROVAL_REVIEW_MAP.ACCEPT,
                    APPROVAL_REVIEW_MAP.NEED_INFO,
                    APPROVAL_REVIEW_MAP.NEED_CHANGES,
                ],
            ],
            [REVIEW_STATUS.NEED_INFO]: [
                [
                    APPROVAL_REVIEW_MAP.RE_SUBMIT,
                    APPROVAL_REVIEW_MAP.CANCEL_REQUEST,
                    APPROVAL_REVIEW_MAP.ADD_COMMENT,
                ],
                [APPROVAL_REVIEW_MAP.ACCEPT, APPROVAL_REVIEW_MAP.NEED_CHANGES],
            ],
            [REVIEW_STATUS.NEED_CHANGES]: [
                [
                    APPROVAL_REVIEW_MAP.RE_SUBMIT,
                    APPROVAL_REVIEW_MAP.CANCEL_REQUEST,
                    APPROVAL_REVIEW_MAP.ADD_COMMENT,
                ],
                [APPROVAL_REVIEW_MAP.ACCEPT, APPROVAL_REVIEW_MAP.NEED_INFO],
            ],
            [REVIEW_STATUS.ACCEPTED]: [
                [APPROVAL_REVIEW_MAP.CANCEL_REQUEST, APPROVAL_REVIEW_MAP.ADD_COMMENT],
                [APPROVAL_REVIEW_MAP.NEED_INFO, APPROVAL_REVIEW_MAP.NEED_CHANGES],
            ],
        };

        const actions: {
            requestorActions: ApprovalReviewMapValues[];
            reviewerActions: ApprovalReviewMapValues[];
        } = {
            requestorActions: [],
            reviewerActions: [],
        };

        // If the allowed actions for requested status exist,
        // then add them to actions
        if (allowedActions[status]) {
            [actions.requestorActions, actions.reviewerActions] = allowedActions[status];
        }

        // If reject action needs to be added, check if the current status of the request
        // falls under the allowed reject conditions.
        if (
            status !== REVIEW_STATUS.CANCELED &&
            ([
                REVIEW_STATUS.PENDING_ACCEPTANCE.valueOf(),
                REVIEW_STATUS.NEED_INFO.valueOf(),
                REVIEW_STATUS.NEED_CHANGES.valueOf(),
            ].includes(status) ||
                (REVIEW_STATUS.ACCEPTED &&
                    this.item()?.currentStatus === WORKFLOW_STATUS.PENDING_APPROVAL))
        ) {
            actions.reviewerActions = [
                ...actions.reviewerActions,
                APPROVAL_REVIEW_MAP.REJECT,
                APPROVAL_REVIEW_MAP.ADD_COMMENT,
            ];
        }

        if (this.item()?.currentStatus === WORKFLOW_STATUS.PENDING_APPROVAL) {
            actions.reviewerActions = [...actions.reviewerActions, APPROVAL_REVIEW_MAP.ADD_COMMENT];
            actions.requestorActions = [
                ...actions.requestorActions,
                APPROVAL_REVIEW_MAP.ADD_COMMENT,
            ];
        }

        return actions;
    }

    private getReviewType(item: Review): APPROVAL_TYPE {
        return item?.inputs?.length === 0 ? APPROVAL_TYPE.INDIVIDUAL : APPROVAL_TYPE.GROUPED;
    }
}
